import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Pencil, PlaneTakeoff, Plane, ShieldAlert, Briefcase, Beaker,
  TrendingUp, Lock, User,
} from "lucide-react";

/**
 * IntlBadges — visa/PSW/route summary chips shown at the top of the
 * "About me" card AND next to each match card in AdvancedResults.
 * Extracted with AboutMeCard from AdvancedResults.jsx on 2026-02-28
 * to slim the parent page. Exported as a named export so ResultCard
 * (still in AdvancedResults.jsx) can consume it too.
 */
export function IntlBadges({ intl }) {
  if (!intl) return null;
  const items = [];
  if (intl.visa_eligible) {
    items.push({
      key: "visa", icon: <Plane className="w-3 h-3" />,
      label: "Visa sponsor OK",
      cls: "bg-emerald-50 text-emerald-800 border-emerald-200",
      testid: "intl-badge-visa-ok",
    });
  } else {
    items.push({
      key: "no-visa", icon: <ShieldAlert className="w-3 h-3" />,
      label: "No sponsor licence",
      cls: "bg-red-50 text-red-800 border-red-200",
      testid: "intl-badge-no-visa",
    });
  }
  if (intl.psw_eligible_years && intl.psw_eligible_years > 0) {
    const y = Number(intl.psw_eligible_years);
    const strong = y >= 2;
    items.push({
      key: "psw", icon: <Briefcase className="w-3 h-3" />,
      label: `PSW ${y % 1 === 0 ? y : y.toFixed(1)}y`,
      cls: strong ? "bg-indigo-50 text-indigo-800 border-indigo-200" : "bg-stone-100 text-stone-700 border-stone-200",
      testid: "intl-badge-psw",
    });
  }
  if (intl.stem_designated) {
    items.push({
      key: "stem", icon: <Beaker className="w-3 h-3" />,
      label: "STEM designated",
      cls: "bg-violet-50 text-violet-800 border-violet-200",
      testid: "intl-badge-stem",
    });
  }
  if (intl.shortage_occupation) {
    items.push({
      key: "shortage", icon: <TrendingUp className="w-3 h-3" />,
      label: "Shortage occupation",
      cls: "bg-amber-50 text-amber-900 border-amber-200",
      testid: "intl-badge-shortage",
    });
  }
  if ((intl.security_clearance_restrictions || []).length > 0) {
    items.push({
      key: "clearance", icon: <Lock className="w-3 h-3" />,
      label: "Clearance restrictions",
      cls: "bg-stone-100 text-stone-700 border-stone-300",
      testid: "intl-badge-clearance",
    });
  }
  if (items.length === 0) return null;
  return (
    <div className="mt-4 flex flex-wrap gap-1.5" data-testid="intl-badges-row">
      {items.map((it) => (
        <span key={it.key} data-testid={it.testid}
              className={`inline-flex items-center gap-1 text-[10px] font-medium border rounded-full px-2 py-0.5 ${it.cls}`}>
          {it.icon}{it.label}
        </span>
      ))}
    </div>
  );
}


// ---------------------------------------------------------------------------
// Internal helpers used by AboutMeCard's expanded detail view. Moved here
// from AdvancedResults.jsx on 2026-02-28 as part of the refactor — they
// are no longer needed anywhere else in the app.
// ---------------------------------------------------------------------------

function KV({ label, value, testid }) {
  // Compact key/value cell — only renders when `value` is meaningful.
  if (value === null || value === undefined || value === "" ||
      (Array.isArray(value) && value.length === 0)) {
    return null;
  }
  return (
    <div data-testid={testid}>
      <div className="label-uppercase !text-[9px] text-stone-500">{label}</div>
      <div className="text-sm text-stone-900 mt-0.5">{value}</div>
    </div>
  );
}

function FitBar({ axis, value }) {
  const pct = Math.max(0, Math.min(10, Number(value) || 0)) * 10;
  return (
    <div className="flex items-center gap-3 text-xs">
      <div className="w-28 shrink-0 text-stone-700 capitalize">{axis}</div>
      <div className="flex-1 h-2 bg-stone-200 rounded-full overflow-hidden">
        <div className="h-full bg-stone-900" style={{ width: `${pct}%` }} />
      </div>
      <div className="w-8 shrink-0 text-right font-mono text-stone-600">{value}/10</div>
    </div>
  );
}

function Section({ title, children, testid }) {
  return (
    <section data-testid={testid} className="border-t border-stone-200 pt-5 mt-5 first:border-0 first:pt-0 first:mt-0">
      <div className="label-uppercase text-stone-600">{title}</div>
      <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-4">
        {children}
      </div>
    </section>
  );
}


/**
 * AboutMeCard — the "profile driving these results" panel at the top
 * of the Advanced Results page. Renders a human-readable summary
 * sentence, a flag list, and a collapsible detailed audit view.
 *
 * Extracted from AdvancedResults.jsx on 2026-02-28 to slim the
 * parent page from ~1810 lines to ~1200. Props unchanged.
 */
export default function AboutMeCard({ profile, onEdit, format, cities = [] }) {
  const [detailOpen, setDetailOpen] = useState(false);
  if (!profile) return null;

  const money = (n) => (typeof n === "number" && n > 0 ? format(n) : null);
  const passports = (profile.passports || []).filter(Boolean);
  const preferred = (profile.preferred_countries || []).filter(Boolean);
  const intendedRaw = profile.intended_degree;
  const intended = Array.isArray(intendedRaw)
    ? intendedRaw.filter(Boolean).join(", ")
    : (intendedRaw || "");

  const CURRICULUM = {
    a_level: "A-levels", ib: "IB", cbse: "CBSE",
    american_diploma: "American Diploma / SAT",
  };
  const gradesShort = (() => {
    if (profile.curriculum === "a_level" && profile.a_level)
      return `${profile.a_level} at A-level`;
    if (profile.curriculum === "ib" && profile.ib)
      return `${profile.ib}/45 in the IB`;
    if (profile.curriculum === "american_diploma" && profile.gpa)
      return `GPA ${profile.gpa}${profile.sat ? ` / SAT ${profile.sat}` : ""}`;
    if (profile.curriculum === "cbse" && profile.cbse)
      return `${profile.cbse}% (CBSE)`;
    return CURRICULUM[profile.curriculum] || "Grades not yet set";
  })();
  const englishLabel = profile.english_waived
    ? "English-test waiver"
    : (profile.ielts ? `IELTS ${profile.ielts}` :
       profile.toefl ? `TOEFL ${profile.toefl}` : null);

  const FUNDS = {
    family: "Family support", savings: "Personal savings",
    loan: "Education loan", third_party: "Third-party sponsor",
  };
  const CAMPUS = {
    city: "City-integrated", campus: "Traditional campus", either: "No preference",
  };

  // Build subject-grade list for the Academic section (A-level or IB).
  const subjectGrades = [];
  if (profile.curriculum === "a_level") {
    for (const [k, label] of [
      ["a_level_maths", "Maths"], ["a_level_further_maths", "Further Maths"],
      ["a_level_english", "English"], ["a_level_physics", "Physics"],
      ["a_level_chemistry", "Chemistry"],
    ]) if (profile[k]) subjectGrades.push(`${label} ${profile[k]}`);
  }
  if (profile.curriculum === "ib") {
    for (const [k, label] of [
      ["ib_maths", "Maths HL"], ["ib_english", "English"],
      ["ib_physics", "Physics"], ["ib_chemistry", "Chemistry"],
    ]) if (profile[k]) subjectGrades.push(`${label} ${profile[k]}/7`);
  }

  // Human-readable sentence at the top — kept as an at-a-glance summary.
  const chunks = [];
  if (passports.length) {
    chunks.push(
      `${passports.length === 1 ? passports[0] : passports.join(" + ")} passport holder`
      + (profile.residence_country && !passports.includes(profile.residence_country)
        ? `, currently living in ${profile.residence_country}` : "")
    );
  } else if (profile.residence_country) {
    chunks.push(`Based in ${profile.residence_country}`);
  }
  if (intended) chunks.push(`aiming for ${intended}`);
  chunks.push(gradesShort);
  if (englishLabel) chunks.push(englishLabel);
  if (preferred.length) {
    chunks.push(
      `looking at ${preferred.length === 1 ? preferred[0] :
        preferred.slice(0, -1).join(", ") + " and " + preferred.slice(-1)}`
    );
  }
  if (cities.length) {
    chunks.push(`with ${cities.length === 1 ? cities[0] : cities.slice(0, -1).join(", ") + " or " + cities.slice(-1)} as ${cities.length === 1 ? "your preferred city" : "preferred cities"}`);
  }
  const budgetSummary = [
    money(profile.max_annual_tuition_usd) && `up to ${money(profile.max_annual_tuition_usd)}/yr tuition`,
    money(profile.total_family_budget_usd) && `${money(profile.total_family_budget_usd)} total`,
    profile.scholarship_required && "needs a scholarship",
  ].filter(Boolean).join(", ");
  if (budgetSummary) chunks.push(budgetSummary);
  const sentence = chunks.filter(Boolean).join(", ") + ".";

  // Boolean flag chips summarising the "extras" the engine treats as gates
  // or preferences. Only true values are rendered.
  const flags = [];
  if (profile.wants_placement) flags.push("wants a placement year");
  if (profile.accreditation_important) flags.push("accreditation matters");
  if (profile.dependants_accompanying) flags.push("bringing dependants");
  if (profile.prior_visa_refusal) flags.push("prior visa refusal");
  if (profile.psw_importance >= 4) flags.push("post-study work is critical");
  if (profile.campus_pref && profile.campus_pref !== "either")
    flags.push(`prefers a ${profile.campus_pref} campus`);
  if (profile.start_months_away && profile.start_months_away < 6)
    flags.push("starting in under 6 months");
  if (profile.study_gap_months && profile.study_gap_months >= 12)
    flags.push("has a study gap of 12+ months");

  // Sorted fit weights (drop unset axes, cap at 7).
  const weightEntries = Object.entries(profile.fit_weights || {})
    .filter(([, v]) => Number(v) > 0)
    .sort((a, b) => Number(b[1]) - Number(a[1]));

  return (
    <div
      data-testid="about-me-card"
      className="mt-6 rounded-lg border border-stone-300 bg-white p-6"
    >
      {/* Header row: icon + editorial sentence + Change me */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <div className="mt-1 rounded-full bg-stone-900 text-white p-2 shrink-0">
            <User className="w-4 h-4" />
          </div>
          <div className="min-w-0">
            <div className="label-uppercase text-stone-500">About you — the profile driving these results</div>
            <p
              className="mt-2 font-editorial text-xl leading-relaxed text-stone-900 max-w-3xl"
              data-testid="about-me-sentence"
            >
              {sentence}
            </p>
            {flags.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-1.5" data-testid="about-me-flags">
                {flags.map((f) => (
                  <span
                    key={f}
                    className="text-[11px] font-underground-sc tracking-wider bg-stone-100 border border-stone-200 rounded-full px-2.5 py-1 text-stone-700"
                  >
                    {f}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
        <Button
          variant="outline"
          onClick={onEdit}
          data-testid="about-me-change-btn"
          className="shrink-0"
        >
          <Pencil className="h-4 w-4 mr-1.5" />
          Change me
        </Button>
      </div>

      {/* Detail sections — collapsed by default; keeps the top of the
          results page focused on the actual matches instead of an audit
          trail. Users pop it open with the Show link when they want to
          verify what the engine is scoring against. */}
      <div className="mt-4 border-t border-stone-100 pt-3 flex items-center justify-between">
        <span className="text-xs font-underground-sc tracking-wider text-stone-500">
          Full profile detail
        </span>
        <button
          type="button"
          data-testid="about-me-detail-toggle"
          onClick={() => setDetailOpen((v) => !v)}
          className="text-xs font-underground-sc tracking-wider text-[#4C1D95] hover:underline"
        >
          {detailOpen ? "hide" : "show"}
        </button>
      </div>
      {detailOpen && (
      <div className="mt-6 border-t border-stone-200 pt-6" data-testid="about-me-detail">
        <Section title="Identity & mobility" testid="about-me-section-identity">
          <KV label="Passports" value={passports.join(" + ") || null} testid="am-passports" />
          <KV label="Residence" value={profile.residence_country} testid="am-residence" />
          <KV label="Residence expires in" value={profile.residence_expiry_months ? `${profile.residence_expiry_months} months` : null} testid="am-residence-expiry" />
          <KV label="Current school" value={profile.current_school} testid="am-school" />
          <KV label="Age" value={profile.age_years ? `${profile.age_years} yrs` : null} testid="am-age" />
          <KV label="Study gap" value={profile.study_gap_months !== undefined && profile.study_gap_months !== null
              ? (profile.study_gap_months ? `${profile.study_gap_months} months` : "None") : null} testid="am-study-gap" />
          <KV label="Starts in" value={profile.start_months_away ? `${profile.start_months_away} months` : null} testid="am-start" />
          <KV label="Dependants accompanying" value={profile.dependants_accompanying ? "Yes" : "No"} testid="am-dependants" />
          <KV label="Prior visa refusal" value={profile.prior_visa_refusal ? "Yes — disclose in application" : "No"} testid="am-refusal" />
        </Section>

        <Section title="Academic profile" testid="about-me-section-academic">
          <KV label="Curriculum" value={CURRICULUM[profile.curriculum]} testid="am-curriculum" />
          <KV label="Overall grade" value={gradesShort} testid="am-grade" />
          <KV label="English test" value={englishLabel} testid="am-english" />
          <KV label="Intended subject" value={intended} testid="am-intended" />
          <KV label="Subject grades"
              value={subjectGrades.length ? subjectGrades.join(" · ") : null}
              testid="am-subject-grades" />
          <KV label="Activities strength"
              value={profile.activities_strength ? `${profile.activities_strength}/5` : null}
              testid="am-activities" />
          <KV label="Portfolio / interview readiness"
              value={profile.portfolio_interview_strength ? `${profile.portfolio_interview_strength}/5` : null}
              testid="am-portfolio" />
        </Section>

        <Section title="Financial picture" testid="about-me-section-financial">
          <KV label="Max tuition / yr" value={money(profile.max_annual_tuition_usd)} testid="am-max-tuition" />
          <KV label="Living budget / yr" value={money(profile.annual_living_budget_usd)} testid="am-living-budget" />
          <KV label="Total family budget" value={money(profile.total_family_budget_usd)} testid="am-family-budget" />
          <KV label="Max deposit" value={money(profile.max_deposit_usd)} testid="am-deposit" />
          <KV label="Funds source" value={FUNDS[profile.funds_source]} testid="am-funds-source" />
          <KV label="Scholarship required"
              value={profile.scholarship_required ? "Yes — filter to funded pathways" : "Not required"}
              testid="am-scholarship" />
        </Section>

        <Section title="Priorities & preferences" testid="about-me-section-priorities">
          <KV label="Preferred destinations" value={preferred.join(", ") || null} testid="am-destinations" />
          <KV label="Campus style" value={CAMPUS[profile.campus_pref]} testid="am-campus" />
          <KV label="Post-study work importance"
              value={profile.psw_importance ? `${profile.psw_importance}/5` : null}
              testid="am-psw" />
          <KV label="Accreditation important"
              value={profile.accreditation_important ? "Yes" : "No"}
              testid="am-accred" />
          <KV label="Placement year"
              value={profile.wants_placement ? "Yes" : "Not required"}
              testid="am-placement" />
        </Section>

        {weightEntries.length > 0 && (
          <section className="border-t border-stone-200 pt-5 mt-5" data-testid="about-me-section-fit-weights">
            <div className="label-uppercase text-stone-600">How you weight fit (0–10)</div>
            <p className="text-xs text-stone-500 mt-1">
              These weights are multiplied against every university's fit scores in the Personal layer of the match.
            </p>
            <div className="mt-4 grid md:grid-cols-2 gap-x-8 gap-y-2 max-w-3xl">
              {weightEntries.map(([axis, v]) => (
                <FitBar key={axis} axis={axis} value={v} />
              ))}
            </div>
          </section>
        )}
      </div>
      )}
    </div>
  );
}

